// src/components/admin/AdminSettings.js
import React, { useState } from "react";
import {
  Sparkles,
  AlertTriangle,
  FlaskConical,
  Save,
  ShieldCheck,
  Mail,
  Bell,
  Package,
} from "lucide-react";

const Section = ({ icon: Icon, title, description, children, disabled }) => (
  <section
    className={`bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3 ${
      disabled ? "opacity-50" : ""
    }`}
  >
    <div className="flex items-start gap-3">
      <div className="mt-0.5">
        <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
          <Icon className="w-4 h-4 text-blue-600" />
        </div>
      </div>
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          {disabled && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
              Coming Soon
            </span>
          )}
        </div>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
    </div>
    <div className="pt-1 space-y-3">{children}</div>
  </section>
);

const Row = ({ label, helper, right, disabled }) => (
  <div
    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-1.5 border-b last:border-b-0 border-gray-100 ${
      disabled ? "opacity-50 pointer-events-none" : ""
    }`}
  >
    <div className="space-y-0.5">
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {helper && <p className="text-xs text-gray-500">{helper}</p>}
    </div>
    <div className="flex items-center gap-2">{right}</div>
  </div>
);

const smallInputClasses =
  "w-20 px-2 py-1 border border-gray-200 rounded-md text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500";

const AdminSettings = ({
  requireNotificationSeen,
  setRequireNotificationSeen,
  notifyLowStock,
  setNotifyLowStock,

  notifyTaskAssignedToUser,
  setNotifyTaskAssignedToUser,
  notifyTaskAssignedToGroup,
  setNotifyTaskAssignedToGroup,
  notifyTerritoryAssigned,
  setNotifyTerritoryAssigned,
  notifySampleStatusChanged,
  setNotifySampleStatusChanged,
  notifySampleAssignedToUser,
  setNotifySampleAssignedToUser,
  notifySampleStatusToCreator,
  setNotifySampleStatusToCreator,
  notifySampleStatusToAssignee,
  setNotifySampleStatusToAssignee,
  notifySampleStatusToAdmins,
  setNotifySampleStatusToAdmins,
  notifySampleComment,
  setNotifySampleComment,
  notifyOrderCreatedToAdmins,
  setNotifyOrderCreatedToAdmins,
  notifyOrderStatusToCreator,
  setNotifyOrderStatusToCreator,
  notifyOrderStatusToAssignee,
  setNotifyOrderStatusToAssignee,
  notifyOrderStatusToAdmins,
  setNotifyOrderStatusToAdmins,
  notifyOrderAssignmentToAssignee,
  setNotifyOrderAssignmentToAssignee,
  notifyOrderSentToWarehouse,
  setNotifyOrderSentToWarehouse,
  notifyOrderApproved,
  setNotifyOrderApproved,
  notifyOrderDelivered,
  setNotifyOrderDelivered,
  notifyOrderEmailCreator,
  setNotifyOrderEmailCreator,
  notifyOrderEmailAssignee,
  setNotifyOrderEmailAssignee,
  notifyOrderEmailAdmins,
  setNotifyOrderEmailAdmins,

  notifyNewSampleableItem,
  setNotifyNewSampleableItem,
  notifyMostPopularItemsThisWeek,
  setNotifyMostPopularItemsThisWeek,
  notifyFeaturedItem,
  setNotifyFeaturedItem,

  customerCatalogEnabled,
  setCustomerCatalogEnabled,

  showInventoryPricingToEndUsers,
  setShowInventoryPricingToEndUsers,
  onlyOwnerCanSeeInventoryPricing,
  setOnlyOwnerCanSeeInventoryPricing,
  onlyOwnerCanDeactivateUsers,
  setOnlyOwnerCanDeactivateUsers,
  onlyOwnersCanAddAdmins,
  setOnlyOwnersCanAddAdmins,
  onlyOwnersCanApproveOrders,
  setOnlyOwnersCanApproveOrders,
  onlyOwnersCanSendOrdersToWarehouse,
  setOnlyOwnersCanSendOrdersToWarehouse,
  creatorsCanEditApprovedOrders,
  setCreatorsCanEditApprovedOrders,
  commercialTermsVisibility,
  setCommercialTermsVisibility,
  requireApprovalForAdmins,
  setRequireApprovalForAdmins,
  gridsVisibility,
  setGridsVisibility,

  autoApproveUnder,
  setAutoApproveUnder,
  maxItemsPerOrder,
  setMaxItemsPerOrder,
  blockDuplicateRequestsEnabled,
  setBlockDuplicateRequestsEnabled,
  blockDuplicateDays,
  setBlockDuplicateDays,
  requireReasonForSample,
  setRequireReasonForSample,

  followUpDaysAfterCompleted,
  setFollowUpDaysAfterCompleted,

  followUpTimeOfDay,
  setFollowUpTimeOfDay,

  savingFeatures,
  onSaveBasicFlags,
}) => {
  const [settings, setSettings] = useState({
    notifyOnApproved: true,
    notifyOnDelivered: true,
    dailyDigestEnabled: false,
    pushImportantAlertsOnly: false,
    labsSuggestFrequentlySampledTogether: false,
    labsAutoCreateFollowupTasks: false,
    labsShowLikelihoodToConvert: false,
  });

  const handleCheckboxChange = (key) => (e) => {
    const value = e.target.checked;
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveAdvanced = () => {};

  return (
    <div className="admin-settings-shell space-y-4 mt-1">
      <p className="text-[11px] text-gray-500">
        Configure workflows, notifications, and experimental features.
        <span className="font-medium text-gray-700"> Live features</span> are wired into app behavior today.
        <span className="font-medium text-gray-700"> Coming Soon</span> sections are placeholders, and any limited-beta rules are labeled in the helper text.
      </p>

      {/* Basic feature flags (LIVE) */}
      <Section
        icon={Sparkles}
        title="Core Feature Flags"
        description="These switches are live and control app behavior immediately."
      >
        <Row
          label='Require notifications to be clicked as "seen"'
          helper="If disabled, opening notifications will auto-mark them as read."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!requireNotificationSeen}
              onChange={(e) => setRequireNotificationSeen(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify end users when sampleable items are low stock"
          helper="Trigger alerts when items marked as sampleable fall below their low stock threshold."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!notifyLowStock}
              onChange={(e) => setNotifyLowStock(e.target.checked)}
            />
          }
        />

        <Row
          label="Allow customers to view the catalog tab"
          helper="If disabled, the Catalog tab in the customer sample portal stays greyed out and inaccessible."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!customerCatalogEnabled}
              onChange={(e) => setCustomerCatalogEnabled(e.target.checked)}
            />
          }
        />

        <Row
          label="Show inventory pricing to end users"
          helper="When enabled, end users can see the Price column in the inventory table. When disabled, only admins/owners see pricing."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!showInventoryPricingToEndUsers}
              onChange={(e) => setShowInventoryPricingToEndUsers(e.target.checked)}
            />
          }
        />

        <Row
          label="Only owner can see inventory pricing"
          helper="Off by default. When enabled, pricing is hidden for admins and end users everywhere in inventory. Only the owner sees it."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!onlyOwnerCanSeeInventoryPricing}
              onChange={(e) => setOnlyOwnerCanSeeInventoryPricing(e.target.checked)}
            />
          }
        />

        <Row
          label="Only owner can deactivate users"
          helper="On by default. When enabled, only the owner can deactivate or reactivate staff accounts in Team."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!onlyOwnerCanDeactivateUsers}
              onChange={(e) => setOnlyOwnerCanDeactivateUsers(e.target.checked)}
            />
          }
        />

        <Row
          label="Only owners can add admins"
          helper="On by default. When enabled, only the owner can invite staff or promote users into admin seats."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!onlyOwnersCanAddAdmins}
              onChange={(e) => setOnlyOwnersCanAddAdmins(e.target.checked)}
            />
          }
        />

        <Row
          label="Visibility on rebates, volume discounts"
          helper='Controls who can see rebate, volume discount, and contract details on inventory items. "None" means owner only.'
          right={
            <select
              className="px-2 py-1 border border-gray-200 rounded-md text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={commercialTermsVisibility || "none"}
              onChange={(e) => setCommercialTermsVisibility(e.target.value)}
            >
              <option value="none">None</option>
              <option value="admin">Admin Only</option>
              <option value="all">All</option>
            </select>
          }
        />

        <Row
          label="Require approval for admins"
          helper="Off by default. When enabled, admin and owner sample orders stay pending until approved."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!requireApprovalForAdmins}
              onChange={(e) => setRequireApprovalForAdmins(e.target.checked)}
            />
          }
        />

        <Row
          label="Only owners can approve orders"
          helper="Off by default. When enabled, admins can still work orders, but only owners can move Pending Approval to Approved."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!onlyOwnersCanApproveOrders}
              onChange={(e) => setOnlyOwnersCanApproveOrders(e.target.checked)}
            />
          }
        />

        <Row
          label="Only owners can send orders to warehouse"
          helper="Off by default. When enabled, only owners can move Approved orders into Sent to Warehouse."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!onlyOwnersCanSendOrdersToWarehouse}
              onChange={(e) => setOnlyOwnersCanSendOrdersToWarehouse(e.target.checked)}
            />
          }
        />

        <Row
          label="Allow creators to edit after approval"
          helper="Off by default. When enabled, the order creator can still update original order details after approval, but cannot reassign the order."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!creatorsCanEditApprovedOrders}
              onChange={(e) => setCreatorsCanEditApprovedOrders(e.target.checked)}
            />
          }
        />

        <Row
          label="Who can view grids"
          helper="Controls who can access the new spreadsheet-style Grids area for inventory and accounts."
          right={
            <select
              className="px-2 py-1 border border-gray-200 rounded-md text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={gridsVisibility || "all_admins"}
              onChange={(e) => setGridsVisibility(e.target.value)}
            >
              <option value="owner_only">Owner Only</option>
              <option value="all_admins">All Admins</option>
              <option value="all_users">All Users</option>
            </select>
          }
        />

        <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500 max-w-md">
            These flags are stored on your company document. Changes take effect immediately across the app.
          </p>
          <button
            type="button"
            onClick={onSaveBasicFlags}
            disabled={savingFeatures}
            className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {savingFeatures ? "Saving…" : "Save feature flags"}
          </button>
        </div>
      </Section>

      {/* In-app notification toggles (LIVE) */}
      <Section
        icon={Bell}
        title="In-app Notification Rules"
        description="These toggles control which in-app notifications are generated. Defaults are ON."
      >
        <Row
          label="Notify when a task is assigned to a user"
          helper="Creates a notification for the specific assignee when assignedTo changes to them."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifyTaskAssignedToUser !== false}
              onChange={(e) => setNotifyTaskAssignedToUser(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify when a task is assigned to a group the user is in"
          helper="Creates notifications for all members of that group when assignedGroupIds includes their team."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifyTaskAssignedToGroup !== false}
              onChange={(e) => setNotifyTaskAssignedToGroup(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify when a user is assigned a new territory"
          helper="When territory assignment changes, the affected user gets a notification."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifyTerritoryAssigned !== false}
              onChange={(e) => setNotifyTerritoryAssigned(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify when a sample order changes status"
          helper="Sends an in-app notification when sample status moves (Approved, Delivered, Completed, etc.)."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifySampleStatusChanged !== false}
              onChange={(e) => setNotifySampleStatusChanged(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify assigned user when a sample order is assigned"
          helper="Lets the assignee know immediately when a sample request is routed to them."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifySampleAssignedToUser !== false}
              onChange={(e) => setNotifySampleAssignedToUser(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify creator when sample order status changes"
          helper="Keeps the person who requested or created the sample informed as it moves through the flow."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifySampleStatusToCreator !== false}
              onChange={(e) => setNotifySampleStatusToCreator(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify assigned user when sample order status changes"
          helper="Useful when one rep owns follow-through on that sample request."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifySampleStatusToAssignee !== false}
              onChange={(e) => setNotifySampleStatusToAssignee(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify owners and admins on sample order status changes"
          helper="On by default so leadership can keep an eye on request movement for launch."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifySampleStatusToAdmins !== false}
              onChange={(e) => setNotifySampleStatusToAdmins(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify on sample order comments"
          helper="Keeps admins, owners, and the assigned rep in the loop when a new sample comment is added."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifySampleComment !== false}
              onChange={(e) => setNotifySampleComment(e.target.checked)}
            />
          }
        />

        <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500 max-w-md">
            These are stored on your company document. Turn off a rule to stop generating those notifications.
          </p>
          <button
            type="button"
            onClick={onSaveBasicFlags}
            disabled={savingFeatures}
            className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {savingFeatures ? "Saving…" : "Save notification rules"}
          </button>
        </div>
      </Section>

      {/* Inventory/Catalog notification toggles (LIVE) */}
      <Section
        icon={Package}
        title="Inventory & Catalog Notifications"
        description="Control notifications for inventory changes and featured items. Defaults are OFF. Only users with group access to items will be notified."
      >
        <Row
          label="Notify when new items become available for sampling"
          helper="Send notifications when an inventory item is marked as sampleable. Only users in the item's groupIds will be notified."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifyNewSampleableItem !== false}
              onChange={(e) => setNotifyNewSampleableItem(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify about most popular items each week"
          helper="Saved now for future scheduled digests. The weekly sender is not active yet, so this toggle is stored but does not currently push a digest on its own."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifyMostPopularItemsThisWeek !== false}
              onChange={(e) => setNotifyMostPopularItemsThisWeek(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify about featured items"
          helper="Send notifications when items are marked as featured. Only users with group access to the item will be notified."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={notifyFeaturedItem !== false}
              onChange={(e) => setNotifyFeaturedItem(e.target.checked)}
            />
          }
        />

        <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500 max-w-md">
            These notifications respect group visibility rules and only notify users who have access to the items.
          </p>
          <button
            type="button"
            onClick={onSaveBasicFlags}
            disabled={savingFeatures}
            className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {savingFeatures ? "Saving…" : "Save inventory notifications"}
          </button>
        </div>
      </Section>

      {/* Follow-up Settings (LIVE) */}
      <Section
        icon={Mail}
        title="Follow-up Settings"
        description="Controls follow-up timing used by automation (emails/tasks). Default is ON at 3 days."
      >
        <Row
          label="Days after delivery to trigger follow-up"
          helper="Default is 3 days. Minimum is 1 full day. Leave BLANK to disable follow-up automation (NO follow-up emails will send and no follow-up task will be created)."
          right={
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <input
                type="number"
                min="1"
                step="1"
                className={smallInputClasses}
                value={followUpDaysAfterCompleted ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFollowUpDaysAfterCompleted(val === "" ? null : Math.max(1, Number(val)));
                }}
                placeholder="3"
              />
              <span>days</span>
            </div>
          }
        />

        <Row
          label="Time of day to schedule the follow-up"
          helper="This is the 'timer' used by automations. Stored as HH:mm."
          right={
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <input
                type="time"
                className="w-28 px-2 py-1 border border-gray-200 rounded-md text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={followUpTimeOfDay ?? "09:00"}
                onChange={(e) => {
                  const val = e.target.value;
                  setFollowUpTimeOfDay(val === "" ? null : val);
                }}
              />
            </div>
          }
        />

        <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500 max-w-md">
            Stored on your company document. <span className="font-semibold">If days is blank, NO follow-up emails will send</span> and automation is disabled.
          </p>
          <button
            type="button"
            onClick={onSaveBasicFlags}
            disabled={savingFeatures}
            className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {savingFeatures ? "Saving…" : "Save follow-up settings"}
          </button>
        </div>
      </Section>

      {/* Sample Order Guardrails (LIVE) */}
      <Section
        icon={ShieldCheck}
        title="Sample Order Guardrails"
        description="Control approval workflows and limits for sample orders. These settings are live."
      >
        <Row
          label="Auto-approve sample orders under"
          helper="Fast lane for small, low-risk sample requests. Set to 0 so all orders require approval."
          right={
            <>
              <input
                type="checkbox"
                className="h-4 w-4 text-blue-600"
                checked={autoApproveUnder !== null}
                onChange={(e) => {
                  if (e.target.checked) setAutoApproveUnder(50);
                  else setAutoApproveUnder(null);
                }}
              />
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <span>$</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={smallInputClasses}
                  value={autoApproveUnder ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAutoApproveUnder(val === "" ? null : Number(val));
                  }}
                  disabled={autoApproveUnder === null}
                  placeholder="50"
                />
              </div>
            </>
          }
        />

        <Row
          label="Limit items per sample order"
          helper="Prevents massive 'shopping cart' style sample requests."
          right={
            <>
              <input
                type="checkbox"
                className="h-4 w-4 text-blue-600"
                checked={maxItemsPerOrder !== null}
                onChange={(e) => {
                  if (e.target.checked) setMaxItemsPerOrder(10);
                  else setMaxItemsPerOrder(null);
                }}
              />
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <span>Max</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className={smallInputClasses}
                  value={maxItemsPerOrder ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMaxItemsPerOrder(val === "" ? null : Number(val));
                  }}
                  disabled={maxItemsPerOrder === null}
                  placeholder="10"
                />
                <span>items</span>
              </div>
            </>
          }
        />

        <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500 max-w-md">
            These guardrails help control sample order workflows and prevent abuse.
          </p>
          <button
            type="button"
            onClick={onSaveBasicFlags}
            disabled={savingFeatures}
            className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {savingFeatures ? "Saving…" : "Save guardrails"}
          </button>
        </div>
      </Section>

      {/* PLACEHOLDER sections unchanged */}
      <Section
        icon={AlertTriangle}
        title="Advanced Workflow & Guardrails"
        description="Additional workflow controls that prevent duplicate requests and tighten request quality."
      >
        <Row
          label="Block duplicate sample requests within ___ days"
          helper="Warn or block if a restaurant requests the same item again too soon."
          right={
            <>
              <input
                type="checkbox"
                className="h-4 w-4 text-blue-600"
                checked={!!blockDuplicateRequestsEnabled}
                onChange={(e) => setBlockDuplicateRequestsEnabled(e.target.checked)}
              />
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <input
                  type="number"
                  min="1"
                  className={smallInputClasses}
                  value={blockDuplicateDays ?? ""}
                  onChange={(e) =>
                    setBlockDuplicateDays(e.target.value === "" ? null : Number(e.target.value))
                  }
                  disabled={!blockDuplicateRequestsEnabled}
                />
                <span>days</span>
              </div>
            </>
          }
        />

        <Row
          label="Require reason for sample"
          helper="Force requesters to explain why they need this sample."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!requireReasonForSample}
              onChange={(e) => setRequireReasonForSample(e.target.checked)}
            />
          }
        />

        <Row
          label="Require target menu placement date"
          helper="Ask when they plan to put this item on the menu."
          disabled
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={false}
              onChange={() => {}}
              disabled
            />
          }
        />

        <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500 max-w-md">
            Duplicate blocking and reason requirements save with the rest of your sample order guardrails.
          </p>
          <button
            type="button"
            onClick={onSaveBasicFlags}
            disabled={savingFeatures}
            className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {savingFeatures ? "Saving…" : "Save workflow rules"}
          </button>
        </div>
      </Section>

      <Section
        icon={Mail}
        title="Order Emails"
        description="Control which real-order milestones send email and who gets those updates."
      >
        <Row
          label="Send email when order is sent to warehouse"
          helper="Useful for internal handoff visibility once an approved order enters warehouse flow."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!notifyOrderSentToWarehouse}
              onChange={(e) => setNotifyOrderSentToWarehouse(e.target.checked)}
            />
          }
        />

        <Row
          label="Send email when order is approved"
          helper="Sends an approval email using the recipient rules below."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!notifyOrderApproved}
              onChange={(e) => setNotifyOrderApproved(e.target.checked)}
            />
          }
        />

        <Row
          label="Send email when order is delivered"
          helper="Sends a delivery email using the recipient rules below."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!notifyOrderDelivered}
              onChange={(e) => setNotifyOrderDelivered(e.target.checked)}
            />
          }
        />

        <div className="pt-2">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400">
            Recipients
          </div>

          <Row
            label="Email order creator"
            helper="Usually the customer-facing or internal person who placed the order."
            right={
              <input
                type="checkbox"
                className="h-4 w-4 text-blue-600"
                checked={!!notifyOrderEmailCreator}
                onChange={(e) => setNotifyOrderEmailCreator(e.target.checked)}
              />
            }
          />

          <Row
            label="Email assigned staff"
            helper="Useful when one person owns warehouse or delivery follow-through."
            right={
              <input
                type="checkbox"
                className="h-4 w-4 text-blue-600"
                checked={!!notifyOrderEmailAssignee}
                onChange={(e) => setNotifyOrderEmailAssignee(e.target.checked)}
              />
            }
          />

          <Row
            label="Email owners and admins"
            helper="Off by default to keep launch emails quieter unless leadership wants copies."
            right={
              <input
                type="checkbox"
                className="h-4 w-4 text-blue-600"
                checked={!!notifyOrderEmailAdmins}
                onChange={(e) => setNotifyOrderEmailAdmins(e.target.checked)}
              />
            }
          />
        </div>

        <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500 max-w-md">
            Keep this simple for launch: choose the key milestones and exactly who should get them.
          </p>
          <button
            type="button"
            onClick={onSaveBasicFlags}
            disabled={savingFeatures}
            className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {savingFeatures ? "Saving…" : "Save order email settings"}
          </button>
        </div>
      </Section>

      <Section
        icon={Bell}
        title="Order In-app Notifications"
        description="These are in-app notifications for operational visibility. They default on for launch."
      >
        <Row
          label="Notify owners and admins when a new order is created"
          helper="Creates an in-app alert for the approval side as soon as a new order lands."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!notifyOrderCreatedToAdmins}
              onChange={(e) => setNotifyOrderCreatedToAdmins(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify creator on order status changes"
          helper="Keeps the person who placed the order updated as it moves through the workflow."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!notifyOrderStatusToCreator}
              onChange={(e) => setNotifyOrderStatusToCreator(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify assigned staff on order status changes"
          helper="Useful when one person owns fulfillment or delivery follow-through."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!notifyOrderStatusToAssignee}
              onChange={(e) => setNotifyOrderStatusToAssignee(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify owners and admins on every order status change"
          helper="On by default for launch so leadership can watch operational flow closely."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!notifyOrderStatusToAdmins}
              onChange={(e) => setNotifyOrderStatusToAdmins(e.target.checked)}
            />
          }
        />

        <Row
          label="Notify assignee when an order is assigned"
          helper="Sends the assigned internal user an in-app alert when ownership changes to them."
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={!!notifyOrderAssignmentToAssignee}
              onChange={(e) => setNotifyOrderAssignmentToAssignee(e.target.checked)}
            />
          }
        />

        <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500 max-w-md">
            These only affect real orders, not sample orders, and they default on unless you turn them off.
          </p>
          <button
            type="button"
            onClick={onSaveBasicFlags}
            disabled={savingFeatures}
            className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {savingFeatures ? "Saving…" : "Save order notification rules"}
          </button>
        </div>
      </Section>

      <Section
        icon={Sparkles}
        title="Notifications & Follow-up"
        description="Control when users get notified and how much follow-up the system does for you. (Not yet implemented)"
        disabled
      >
        <Row
          label="Send notification when sample is approved"
          helper="Notify the restaurant and assigned rep when a sample is approved."
          disabled
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={settings.notifyOnApproved}
              onChange={handleCheckboxChange("notifyOnApproved")}
              disabled
            />
          }
        />

        <Row
          label="Send notification when sample is delivered"
          helper="Toggle whether delivery notifications are sent and to whom."
          disabled
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={settings.notifyOnDelivered}
              onChange={handleCheckboxChange("notifyOnDelivered")}
              disabled
            />
          }
        />

        <Row
          label="Daily email digest of sample activity"
          helper="One email per day summarizing new, approved, delivered, and converted samples."
          disabled
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={settings.dailyDigestEnabled}
              onChange={handleCheckboxChange("dailyDigestEnabled")}
              disabled
            />
          }
        />

        <Row
          label="Push 'important alerts' only"
          helper="Only show high-priority notification types (e.g., alert, system)."
          disabled
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={settings.pushImportantAlertsOnly}
              onChange={handleCheckboxChange("pushImportantAlertsOnly")}
              disabled
            />
          }
        />
      </Section>

      <Section
        icon={FlaskConical}
        title="Labs (Experimental)"
        description="Fun experiments that make the product feel smart. (Not yet implemented)"
        disabled
      >
        <Row
          label="[Labs] Suggest 'Frequently Sampled Together' items"
          helper="When selecting samples, recommend additional items based on past activity."
          disabled
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={settings.labsSuggestFrequentlySampledTogether}
              onChange={handleCheckboxChange("labsSuggestFrequentlySampledTogether")}
              disabled
            />
          }
        />

        <Row
          label="[Labs] Auto-create follow-up tasks for reps"
          helper="Create tasks automatically when samples are approved so nothing falls through."
          disabled
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={settings.labsAutoCreateFollowupTasks}
              onChange={handleCheckboxChange("labsAutoCreateFollowupTasks")}
              disabled
            />
          }
        />

        <Row
          label="[Labs] Show 'Likelihood to Convert' score"
          helper="Simple heuristic score based on restaurant and product history."
          disabled
          right={
            <input
              type="checkbox"
              className="h-4 w-4 text-blue-600"
              checked={settings.labsShowLikelihoodToConvert}
              onChange={handleCheckboxChange("labsShowLikelihoodToConvert")}
              disabled
            />
          }
        />

        <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-500 max-w-md">
            These are placeholders for now. Once you like how they feel, we'll connect them to real scoring and automation.
          </p>
          <button
            type="button"
            onClick={handleSaveAdvanced}
            disabled
            className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-[11px] font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-3.5 h-3.5" />
            Save advanced rules
          </button>
        </div>
      </Section>
    </div>
  );
};

export default AdminSettings;
